import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSiteSettings } from '@/hooks/useSiteSettings';

interface FinishingOption {
  id: string;
  name: string;
  description: string;
  minOrder: number;
  leadTimeDays: number;
  colors: string[];
}

const FINISHING_META = [
  { id: 'embossed', label: '엠보싱' },
  { id: 'foil', label: '형압' },
  { id: 'uv', label: 'UV코팅' },
  { id: 'diecut', label: '톰슨' },
  { id: 'frosting', label: '프로스팅' },
];

interface ImageGalleryProps {
  images: string[];
  productName: string;
  productId?: string;
  finishingOptions?: FinishingOption[];
}

export default function ImageGallery({ images, productName, productId = '', finishingOptions = [] }: ImageGalleryProps) {
  const { t } = useTranslation('products');
  const [activeIndex, setActiveIndex] = useState(0);
  const [imgErrors, setImgErrors] = useState<Set<number>>(new Set());
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { settings } = useSiteSettings();

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleImgError = (idx: number) => {
    setImgErrors((prev) => new Set(prev).add(idx));
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Main Image */}
      <div className="relative aspect-square bg-cream-50 rounded-lg overflow-hidden border border-ink-100 flex items-center justify-center">
        {images.length === 0 ? (
          <div className="flex flex-col items-center justify-center">
            <i className="ri-image-line text-5xl text-ink-200 mb-3" />
            <span className="text-sm text-ink-300">{t('detail.noImage')}</span>
          </div>
        ) : !imgErrors.has(activeIndex) ? (
          <img
            src={images[activeIndex]}
            alt={`${productName} - ${activeIndex + 1}`}
            className="w-full h-full object-contain p-8"
            onError={() => handleImgError(activeIndex)}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center">
            <i className="ri-image-line text-5xl text-ink-200 mb-3" />
            <span className="text-sm text-ink-300">{t('detail.imageNotAvailable')}</span>
          </div>
        )}

        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={handlePrev}
              className="absolute left-2 md:left-3 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/80 border border-ink-100 flex items-center justify-center hover:bg-white transition-all shadow-sm z-10 cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-lg text-ink-600" />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/80 border border-ink-100 flex items-center justify-center hover:bg-white transition-all shadow-sm z-10 cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-lg text-ink-600" />
            </button>
          </>
        )}

        {/* Dot Indicators */}
        {images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                className={`w-2 h-2 rounded-full transition-all duration-200 cursor-pointer ${
                  idx === activeIndex
                    ? 'bg-ink-700 w-5'
                    : 'bg-ink-200 hover:bg-ink-300'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setActiveIndex(idx)}
              className={`shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-md overflow-hidden border-2 transition-all cursor-pointer ${
                idx === activeIndex
                  ? 'border-warm-500'
                  : 'border-transparent hover:border-ink-200'
              }`}
            >
              {!imgErrors.has(idx) ? (
                <img
                  src={img}
                  alt={`${productName} thumbnail ${idx + 1}`}
                  className="w-full h-full object-contain p-1"
                  onError={() => handleImgError(idx)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-cream-50">
                  <i className="ri-image-line text-lg text-ink-200" />
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Finishing Preview Thumbnails */}
      {images.length > 0 && (
        <div className="mt-3">
          <div className="flex items-center gap-3 mb-2">
            <span className="h-px flex-1 bg-ink-200" />
            <span className="text-xs text-ink-400 font-medium whitespace-nowrap">{t('detail.myFinishingImages')}</span>
            <span className="h-px flex-1 bg-ink-200" />
          </div>
          <div className="grid grid-cols-5 gap-2">
            {FINISHING_META.map((meta, idx) => {
              const option = finishingOptions[idx];
              const label = option?.name || meta.label;
              const thumbUrl = settings.productFinishingThumbnails[meta.id];
              return (
                <button
                  key={meta.id}
                  onClick={() => {
                    if (!user) {
                      navigate('/sign-in');
                      return;
                    }
                    const currentSelected = (location.state as { selectedFinishingIds?: string[] } | null)?.selectedFinishingIds || [];
                    navigate(`/products/${productId}/finishing-preview`, {
                      state: { selectedFinishingIds: currentSelected, activeImageIndex: activeIndex },
                    });
                  }}
                  title={label}
                  className="aspect-square rounded-md overflow-hidden border-2 border-transparent hover:border-warm-400 transition-all cursor-pointer"
                >
                  {thumbUrl ? (
                    <img
                      src={thumbUrl}
                      alt={label}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-cream-50 flex items-center justify-center">
                      <i className="ri-image-line text-xl text-ink-300" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}